import {
  InfiniteData,
  MutationOptions,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import moment from 'moment-timezone';

import { API_URL, queryCreate, queryGet, queryUpdate } from './fetch';

import {
  CacheStateSnapshot,
  PaginatedResults,
  useBatchParticipationStatusUpdate,
  useParticipationStatusUpdate,
  useUpdateIndexedParticipationCache,
  useUpdateParticipationCache,
  useUpdateStatusQueryOptions,
} from './participationCache';
import { DevicesCategory, ParticipationReportReason } from './session';
import {
  IndexedParticipation,
  Message,
  Participation,
  ParticipationSource,
  ParticipationStatus,
} from './participation';
import { useMe } from './auth';
import { FilterableField } from './filter/filter';
import { SelectionPayload } from './participations/selection';

const eventSources: Record<string, EventSource | undefined> = {};

export type ParticipationMutationOptions = MutationOptions<
  Participation,
  Error,
  Participation,
  Participation | ParticipationUpdateData
>;

export interface ParticipationUpdateData extends Participation {
  oldStatus: ParticipationStatus;
  snapshot: CacheStateSnapshot;
}

export const useSessionParticipationListener = (sessionId?: string) => {
  const { data: me } = useMe();
  const updateParticipationCache = useUpdateParticipationCache();

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    if (eventSources[sessionId]) {
      if (eventSources[sessionId]?.readyState !== EventSource.CLOSED) {
        return;
      }
    }
    const eventSource = new EventSource(
      `${API_URL}/sse/sessions/${sessionId}/participations`,
      {
        withCredentials: true,
      },
    );
    eventSource.onmessage = ({ data }) => {
      const participation: Participation = JSON.parse(data);
      if (me?.id === participation?.updateCausedBy) {
        updateParticipationCache(participation);
      } else {
        updateParticipationCache(participation, { isUpdateMutation: true });
      }
    };
    eventSources[sessionId] = eventSource;

    return () => {
      eventSource.close();
      eventSources[sessionId] = undefined;
    };
  }, [sessionId, me, updateParticipationCache]);
};

export const useSessionParticipationOnSSE = (
  sessionId?: string,
  onSSE?: (participation: Participation) => void,
) => {
  const onSSERef = useRef(onSSE);

  useEffect(() => {
    onSSERef.current = onSSE;
  }, [onSSE]);

  useEffect(() => {
    if (!sessionId || !onSSERef.current) {
      return;
    }
    const eventSource = new EventSource(
      `${API_URL}/sse/sessions/${sessionId}/participations`,
      {
        withCredentials: true,
      },
    );

    eventSource.onmessage = ({ data }) => {
      const participation: Participation = JSON.parse(data);
      onSSERef.current?.(participation);
    };

    return () => {
      eventSource.close();
    };
  }, [sessionId]);
};

export const useInfiniteSessionParticipations = (
  id?: string,
  status?: ParticipationStatus[] | ParticipationStatus,
  search?: string,
) => {
  const statusList = Array.isArray(status) ? status : [status];

  const queryKey = search
    ? ['sessions', id, 'participations', 'infinite', statusList, search]
    : ['sessions', id, 'participations', 'infinite', statusList];

  useSessionParticipationListener(id);

  const updateParticipationCache = useUpdateParticipationCache(
    queryKey,
    false,
    true,
  );
  const onSuccess = useCallback(
    (res: InfiniteData<PaginatedResults<Participation>>) => {
      res.pages.forEach(({ data }) => {
        data.forEach((participation) => {
          updateParticipationCache(participation);
        });
      });
    },
    [updateParticipationCache],
  );

  return useInfiniteQuery<PaginatedResults<Participation>>(
    queryKey,
    ({ pageParam }) =>
      queryGet(`/sessions/${id}/participations`, {
        status: statusList,
        cursor: pageParam,
        search,
      })(),
    {
      enabled: !!id,
      onSuccess: onSuccess,
      getNextPageParam: (lastPage, allPages) => {
        const downloadedCount = allPages.reduce((acc, page) => {
          return acc + page.data.length;
        }, 0);
        if (downloadedCount >= lastPage.pagination.count) {
          return undefined;
        }
        return lastPage.pagination.cursor;
      },
    },
  );
};

export const useSessionParticipationsFilterableValues = (
  id?: string,
  fieldRef?: string,
  onlyOldValues?: boolean,
) => {
  const queryKey = useMemo(() => {
    return [
      'sessions',
      id,
      'participations',
      'filterable-field',
      fieldRef,
      onlyOldValues,
    ];
  }, [id, fieldRef, onlyOldValues]);

  return useQuery<FilterableField>(
    queryKey,
    queryGet(`/v2/sessions/${id}/participations/filterable-field`, {
      field: fieldRef,
      onlyOldValues: !!onlyOldValues,
    }),
    {
      enabled: !!id,
    },
  );
};

export const useInfiniteV2SessionParticipations = (
  id?: string,
  status?: ParticipationStatus[] | ParticipationStatus,
  search?: string,
  filters?: Array<{ field: string; type: string; values: string[] }>,
  sort?: { field: string; type: string; direction: 'asc' | 'desc' },
) => {
  const statusList = useMemo(
    () => (Array.isArray(status) ? status : [status]),
    [status],
  );

  const queryClient = useQueryClient();
  const queryKey = useMemo(() => {
    const baseKey = [
      'sessions',
      id,
      'participations',
      'v2',
      'infinite',
      statusList,
    ];
    if (search) {
      baseKey.push(search);
    }
    if (filters && filters.length > 0) {
      baseKey.push(JSON.stringify(filters));
    }
    if (sort) {
      baseKey.push(JSON.stringify(sort));
    }
    return baseKey;
  }, [id, statusList, search, filters, sort]);

  useSessionParticipationListener(id);
  const updateParticipationCache = useUpdateIndexedParticipationCache();
  const onSuccess = useCallback(
    (res: InfiniteData<PaginatedResults<IndexedParticipation>>) => {
      res.pages.forEach(({ data }) => {
        data.forEach((participation) => {
          updateParticipationCache(participation);
        });
      });
    },
    [updateParticipationCache],
  );

  const onError = useCallback(
    (error: AxiosError) => {
      if (error.response?.status === 410) {
        queryClient.resetQueries(queryKey);
      }
    },
    [queryClient, queryKey],
  );

  const reset = useCallback(
    () => queryClient.resetQueries(queryKey),
    [queryClient, queryKey],
  );

  const query = useInfiniteQuery<
    PaginatedResults<IndexedParticipation>,
    AxiosError
  >(
    queryKey,
    ({ pageParam }) => {
      const body: any = {
        status: statusList,
        cursor: pageParam,
        search,
        sort,
      };
      if (filters && filters.length > 0) {
        body.filters = filters;
      }
      return queryCreate(`/v2/sessions/${id}/participations`)(body);
    },
    {
      enabled: !!id,
      onSuccess,
      onError: onError,
      getNextPageParam: (lastPage, allPages) => {
        const loadedCount = allPages.reduce(
          (acc, page) => acc + page.data.length,
          0,
        );

        const totalCount = lastPage.pagination.count;
        if (loadedCount >= totalCount) {
          return undefined;
        }

        return lastPage.pagination.cursor;
      },
    },
  );

  return {
    ...query,
    reset,
  };
};

export const useSessionParticipations = (
  id?: string,
  status?: ParticipationStatus[] | ParticipationStatus,
  pagination: { page: number; pageSize: number } = { page: 1, pageSize: 10 },
  search?: string,
  version?: 1 | 2,
) => {
  const queryClient = useQueryClient();
  const statusList = Array.isArray(status) ? status : [status];

  const queryKey = [
    'sessions',
    id,
    'participations',
    'page',
    statusList,
    version,
    { ...pagination, search },
  ];

  useSessionParticipationListener(id);
  const updateParticipationCache = useUpdateParticipationCache(
    queryKey,
    false,
    true,
  );
  const onSuccess = useCallback(
    (res: PaginatedResults<Participation>) => {
      res?.data?.forEach((participation) => {
        updateParticipationCache(participation);
      });
    },
    [queryClient, queryKey, updateParticipationCache],
  );

  const path =
    version && version === 2
      ? `/v2/sessions/${id}/participations`
      : `/sessions/${id}/participations`;

  return useQuery<PaginatedResults<Participation>>(
    queryKey,
    queryGet(path, {
      status: statusList,
      search,
      ...pagination,
    }),
    {
      enabled: !!id,
      onSuccess,
    },
  );
};

export const useParticipation = (
  id?: string,
  options: UseQueryOptions<Participation, AxiosError> = {},
) => {
  const updateParticipationCache = useUpdateParticipationCache([
    'participations',
    id,
  ]);
  return useQuery<Participation, AxiosError>(
    ['participations', id],
    queryGet(`/participations/${id}`),
    {
      retry: (failureCount, error: AxiosError) =>
        failureCount < 3 &&
        (!error.response ||
          !error.response.status ||
          error.response.status >= 500),
      enabled: !!id,
      onSuccess: updateParticipationCache,
      ...options,
    },
  );
};

export const useParticipationExists = (id?: string) => {
  return useQuery<boolean, AxiosError>(
    ['participations', id, 'exists'],
    queryGet(`/participations/${id}/exists`),
    {
      enabled: !!id,
    },
  );
};

export const useBulkInvite = () => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(() => {
    setTimeout(() => {
      queryClient.invalidateQueries(['invitation-queues']);
    }, 1000);
  }, [queryClient]);

  return useMutation<
    number,
    Error,
    {
      sessionId: string;
      participantIds: string[];
      message?: string;
      subject?: string;
    }
  >(queryCreate('/participations/bulk-invite'), {
    onSuccess,
  });
};

export const useParticipationByApplicationId = (
  applicationId?: string,
  options: UseQueryOptions<Participation, AxiosError> = {},
) => {
  const updateParticipationCache = useUpdateParticipationCache([
    'application',
    applicationId,
    'participations',
  ]);

  return useQuery<Participation, AxiosError>(
    ['application', applicationId, 'participations'],
    queryGet(`/applications/${applicationId}/participation`),
    {
      enabled: !!applicationId,
      onSuccess: updateParticipationCache,
      ...options,
    },
  );
};

export const useSessionBookedParticipations = (id?: string) => {
  const updateParticipationCache = useUpdateParticipationCache([
    'sessions',
    id,
    'participations',
    'booked',
  ]);
  const onSuccess = useCallback(
    (res: Participation[]) => {
      res.forEach((participation) => {
        updateParticipationCache(participation);
      });
    },
    [updateParticipationCache],
  );
  return useQuery<Participation[]>(
    ['sessions', id, 'participations', 'booked'],
    queryGet(`/sessions/${id}/participations/booked`),
    {
      enabled: !!id,
      onSuccess,
    },
  );
};

export const useParticipationMessages = (id?: string) => {
  const esRef = useRef<EventSource | null>(null);
  const messageIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    if (!id) {
      return;
    }
    const eventSource = new EventSource(
      `${API_URL}/sse/participations/${id}/messages`,
      {
        withCredentials: true,
      },
    );

    eventSource.onmessage = (event) => {
      const newMessage = JSON.parse(event.data);
      if (messageIdRef.current === newMessage.id) {
        return;
      }
      messageIdRef.current = newMessage.id;
      queryClient.setQueryData(
        ['participations', id, 'messages'],
        (messages: Message[] = []) => {
          if (
            messages &&
            messages.length !== 0 &&
            newMessage.id !== messages[messages.length - 1].id
          ) {
            return [...messages, newMessage];
          }
          return messages;
        },
      );
    };
    esRef.current = eventSource;
    return () => {
      eventSource.close();
    };
  }, [id, queryClient]);

  return useQuery<Message[]>(
    ['participations', id, 'messages'],
    queryGet(`/participations/${id}/messages`),
    {
      enabled: !!id,
      onSuccess: (messages) => {
        const participantId = messages[0]?.participantId;
        const participation: Participation | undefined =
          queryClient.getQueryData([
            'participations',
            messages[0]?.participationId,
          ]);
        queryClient.invalidateQueries([
          'messages',
          'participant',
          participantId,
          'unread',
        ]);
        queryClient.invalidateQueries([
          'messages',
          'session',
          participation?.sessionId,
          'unread',
        ]);
      },
    },
  );
};

export const useParticipantUnreadMessages = (id?: string) => {
  return useQuery<Message[]>(
    ['messages', 'participant', id, 'unread'],
    queryGet(`/messages/participant/${id}/unread`),
    {
      enabled: !!id,
    },
  );
};

export const useSessionUnreadMessages = (id?: string) => {
  return useQuery<Message[]>(
    ['messages', 'session', id, 'unread'],
    queryGet(`/messages/session/${id}/unread`),
    {
      enabled: !!id,
    },
  );
};

export const useCreateParticipationMessage = (id?: string) => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (newMessage: Message) => {
      queryClient.setQueryData(
        ['participations', id, 'messages'],
        (messages: Message[] = []) => {
          if (!messages || messages.length === 0) {
            return [newMessage];
          }
          if (newMessage.id !== messages[messages.length - 1].id) {
            return [...messages, newMessage];
          }
          return messages;
        },
      );
    },
    [queryClient, id],
  );

  return useMutation<Message, Error, any>(
    queryCreate(`/participations/${id}/messages`),
    {
      onSuccess,
    },
  );
};

export const useDeclineParticipation = () => {
  const update = (p: Participation) => ({
    ...p,
    updatedAt: moment().format(),
    status: ParticipationStatus.DECLINED,
  });
  const options = useUpdateStatusQueryOptions(update);

  return useMutation<Participation, AxiosError, Participation, Participation>(
    (participation: Participation) =>
      queryUpdate(`/participations/${participation.id}/decline`)(undefined),
    options,
  );
};

export const useDeclineParticipationTable = () => {
  const options = useParticipationStatusUpdate(ParticipationStatus.DECLINED);

  return useMutation<
    Participation,
    AxiosError,
    Participation,
    ParticipationUpdateData | Participation
  >(
    (participation: Participation) =>
      queryUpdate(`/participations/${participation.id}/decline`)(undefined),
    options,
  );
};

export const usePayParticipantTable = (sessionId?: string) => {
  const { mutate, mutateAsync, ...rest } = useBulkPayParticipation(sessionId);

  return {
    ...rest,
    mutate: (participation: IndexedParticipation) =>
      mutate({
        sessionId: participation.sessionId,
        selectionMode: 'partial',
        selectedIds: [participation.id],
        selectedParticipations: [participation],
      }),
    mutateAsync: (participation: IndexedParticipation) =>
      mutateAsync({
        sessionId: participation.sessionId,
        selectionMode: 'partial',
        selectedIds: [participation.id],
        selectedParticipations: [participation],
      }),
  };
};

export const useAcceptParticipationTable = () => {
  const options = useParticipationStatusUpdate(ParticipationStatus.ACCEPTED);

  return useMutation<
    Participation,
    AxiosError,
    Participation,
    ParticipationUpdateData | Participation
  >(
    (participation: Participation) =>
      queryUpdate(`/participations/${participation.id}/accept`)(undefined),
    options,
  );
};

export const useBulkDeclineParticipation = (sessionId?: string) => {
  const updateOptions = useParticipationStatusUpdate(
    ParticipationStatus.DECLINED,
  );
  const options = useBatchParticipationStatusUpdate(
    ParticipationStatus.DECLINED,
    updateOptions,
  );

  return useMutation<
    Participation[],
    AxiosError,
    SelectionPayload,
    ParticipationUpdateData[]
  >(
    ({ selectedParticipations, ...data }) =>
      queryUpdate(`/sessions/${sessionId}/participations/decline`)(data),
    options,
  );
};

interface BulkReportParticipationWithPayload extends SelectionPayload {
  reportReason?: ParticipationReportReason;
}

export const useBulkReportParticipation = (sessionId?: string) => {
  const updateOptions = useParticipationStatusUpdate(
    ParticipationStatus.NO_SHOW,
  );
  const options = useBatchParticipationStatusUpdate(
    ParticipationStatus.NO_SHOW,
    updateOptions,
  );

  return useMutation<
    Participation[],
    AxiosError,
    BulkReportParticipationWithPayload,
    ParticipationUpdateData[]
  >(
    ({ selectedParticipations, ...data }) =>
      queryUpdate(`/sessions/${sessionId}/participations/report`)(data),
    options,
  );
};

export const useBulkAcceptParticipation = (sessionId?: string) => {
  const updateOptions = useParticipationStatusUpdate(
    ParticipationStatus.ACCEPTED,
  );
  const options = useBatchParticipationStatusUpdate(
    ParticipationStatus.ACCEPTED,
    updateOptions,
  );

  return useMutation<
    Participation[],
    AxiosError,
    SelectionPayload,
    ParticipationUpdateData[]
  >(
    ({ selectedParticipations, ...data }) =>
      queryUpdate(`/sessions/${sessionId}/participations/accept`)(data),
    options,
  );
};

export const useBulkPayParticipation = (sessionId?: string) => {
  const updateOptions = useParticipationStatusUpdate(
    ParticipationStatus.PROCESSING_PAYMENT,
  );
  const options = useBatchParticipationStatusUpdate(
    ParticipationStatus.PROCESSING_PAYMENT,
    updateOptions,
  );

  return useMutation<
    Participation[],
    AxiosError,
    SelectionPayload,
    ParticipationUpdateData[]
  >(
    ({ selectedParticipations, ...data }) =>
      queryUpdate(`/sessions/${sessionId}/participations/pay`)(data),
    options,
  );
};

export const useAcceptParticipation = (
  optionsParams?: UseMutationOptions<
    Participation,
    Error,
    Participation,
    Participation | ParticipationUpdateData
  >,
) => {
  const update = (p: Participation) => ({
    ...p,
    updatedAt: moment().format(),
    status: ParticipationStatus.ACCEPTED,
  });
  const options = useUpdateStatusQueryOptions(update, optionsParams);

  return useMutation<Participation, Error, Participation, Participation>(
    (participation: Participation) =>
      queryUpdate(`/participations/${participation.id}/accept`)(undefined),
    options,
  );
};

export const useRestoreToAccepted = () => {
  const update = (p: Participation) => ({
    ...p,
    updatedAt: moment().format(),
    status: ParticipationStatus.ACCEPTED,
  });
  const options = useUpdateStatusQueryOptions(update);

  return useMutation<Participation, AxiosError, Participation, Participation>(
    (participation: Participation) =>
      queryUpdate(`/participations/${participation.id}/restore-to-accepted`)(
        undefined,
      ),
    options,
  );
};

export const useUndoDeclineParticipation = () => {
  const update = (p: Participation) => ({
    ...p,
    updatedAt: moment().format(),
    status: ParticipationStatus.APPLIED,
  });
  const statusUpdateOptions = useParticipationStatusUpdate(
    ParticipationStatus.APPLIED,
  );
  const options = useUpdateStatusQueryOptions<Participation>(
    update,
    statusUpdateOptions,
  );

  return useMutation<Participation, AxiosError, Participation, Participation>(
    (participation: Participation) =>
      queryUpdate(`/participations/${participation.id}/undoDecline`)(undefined),
    options,
  );
};

export const useUndoNotInterestedParticipation = (id?: string) => {
  const updateParticipationCache = useUpdateParticipationCache();
  const queryClient = useQueryClient();
  return useMutation<Participation, unknown, void>(
    queryUpdate(`/participations/${id}/undo-not-interested`),
    {
      onSuccess: (participation: Participation) => {
        updateParticipationCache(participation, { isUpdateMutation: true });
        queryClient.invalidateQueries(['participations', participation.id]);
        queryClient.invalidateQueries([
          'participations',
          participation.trackingId,
        ]);
      },
    },
  );
};

export const useBookMeeting = (id?: string) => {
  const updateParticipationCache = useUpdateParticipationCache();
  const queryClient = useQueryClient();

  return useMutation<
    Participation,
    AxiosError,
    {
      date: string;
      selectedDevice?: DevicesCategory | null;
    }
  >(queryUpdate(`/participations/${id}/book`), {
    onSuccess: (participation: Participation) => {
      updateParticipationCache(participation, { isUpdateMutation: true });
      queryClient.invalidateQueries(['participations', participation.id]);
      queryClient.invalidateQueries([
        'participations',
        participation.trackingId,
      ]);
    },
  });
};

interface RescheduleParams extends Participation {
  date: string;
}

export const useReschedule = (
  id?: string,
  optionsParams?: ParticipationMutationOptions,
) => {
  const updateParticipationCache = useUpdateParticipationCache();
  const queryClient = useQueryClient();

  return useMutation<
    Participation,
    AxiosError,
    RescheduleParams,
    Participation | ParticipationUpdateData
  >(queryUpdate(`/participations/${id}/reschedule`), {
    ...optionsParams,
    onSuccess: (participation: Participation) => {
      optionsParams?.onSuccess?.(participation, participation, undefined);
      updateParticipationCache(participation, { isUpdateMutation: true });
      queryClient.invalidateQueries(['participations', participation.id]);
      queryClient.invalidateQueries([
        'participations',
        participation.trackingId,
      ]);
    },
  });
};

export interface CancelParams extends Participation {
  message?: string;
  lastMinuteOverride?: boolean;
}

export const useCancelParticipationByParticipant = () => {
  return useMutation<Participation, AxiosError, CancelParams, Participation>(
    ({ message, ...participation }: CancelParams) =>
      queryUpdate(`/participations/${participation.id}/cancel`, {
        params: {
          byClient: false,
        },
      })({
        message,
      }),
  );
};

export const useCancelParticipationByClient = (
  optionsParams?: UseMutationOptions<
    Participation,
    Error,
    CancelParams,
    Participation | ParticipationUpdateData
  >,
) => {
  const update = (p: Participation) => ({
    ...p,
    appointmentId: undefined,
    appointment: undefined,
    cancelledAppointmentId: p.appointmentId,
    status: ParticipationStatus.CANCELED,
  });
  const options = useUpdateStatusQueryOptions<CancelParams>(
    update,
    optionsParams,
  );

  return useMutation<Participation, AxiosError, CancelParams, Participation>(
    ({ message, lastMinuteOverride, ...participation }: CancelParams) =>
      queryUpdate(`/participations/${participation.id}/cancel`, {
        params: {
          byClient: true,
        },
      })({
        message,
        lastMinuteOverride,
      }),
    options,
  );
};

export const useMarkAsParticipated = (
  optionsParams?: UseMutationOptions<
    Participation,
    Error,
    Participation,
    Participation | ParticipationUpdateData
  >,
) => {
  const update = (p: Participation) => ({
    ...p,
    updatedAt: moment().format(),
    status: ParticipationStatus.PARTICIPATED,
  });
  const options = useUpdateStatusQueryOptions(update, optionsParams);

  return useMutation<Participation, AxiosError, Participation, Participation>(
    (participation: Participation) =>
      queryUpdate(`/participations/${participation.id}/markAsParticipated`)(
        undefined,
      ),
    options,
  );
};

interface PayParams {
  participation: Participation;
}

export const usePayParticipant = (
  optionsParams?: ParticipationMutationOptions,
) => {
  const update = (p: Participation) => ({
    ...p,
    updatedAt: moment().format(),
    status: ParticipationStatus.PAYMENT_REQUESTED,
  });
  const options = useUpdateStatusQueryOptions(update, optionsParams);

  return useMutation<Participation, AxiosError, PayParams, Participation>(
    async ({ participation }: PayParams) => {
      const res = await queryUpdate(
        `/sessions/${participation.sessionId}/participations/pay`,
      )({
        sessionId: participation.sessionId,
        selectionMode: 'partial',
        selectedIds: [participation.id],
      });
      if (!res.length) {
        return participation;
      }
      return res[0];
    },
    {
      onMutate: ({ participation }: PayParams) =>
        options?.onMutate?.(participation),
      onError: (err, { participation }: PayParams, ctx) =>
        options?.onError?.(err, participation, ctx),
      onSuccess: (data, { participation }: PayParams, ctx) =>
        options?.onSuccess?.(data, participation, ctx),
    },
  );
};

interface ReportParams {
  participation: Participation;
  reportReason: ParticipationReportReason;
  shouldReplaceParticipant?: boolean;
}

export const useReportParticipant = (
  optionsParams?: ParticipationMutationOptions,
) => {
  const update = (p: Participation) => ({
    ...p,
    updatedAt: moment().format(),
    status: ParticipationStatus.NO_SHOW,
  });
  const options = useUpdateStatusQueryOptions(update, optionsParams);

  return useMutation<Participation, AxiosError, ReportParams, Participation>(
    ({ participation, reportReason, shouldReplaceParticipant }: ReportParams) =>
      queryUpdate(`/participations/${participation.id}/report`)({
        reportReason,
        shouldReplaceParticipant,
      }),
    {
      onMutate: ({ participation }: ReportParams) =>
        options?.onMutate?.(participation),
      onError: (err: Error, { participation }: ReportParams) =>
        options?.onError?.(err, participation, undefined),
      onSuccess: (data, { participation }: PayParams, ctx) =>
        options?.onSuccess?.(data, participation, ctx),
    },
  );
};

interface InstructionsDto {
  instructions?: string | null;
  videoCallLink?: string | null;
  studyLink?: string | null;
}

type UpdateInsctructionOptions = UseMutationOptions<
  Participation,
  Error,
  InstructionsDto
>;

// @TODO REMOVE
export const useUpdateInstructions = (
  id?: string,
  options: UpdateInsctructionOptions = {},
) => {
  const updateParticipationCache = useUpdateParticipationCache();
  return useMutation<Participation, Error, InstructionsDto>(
    queryUpdate(`/participations/${id}/instructions`),
    {
      onSuccess: (p, variable, context) => {
        updateParticipationCache(p);
        if (options.onSuccess) {
          options.onSuccess(p, variable, context);
        }
      },
      ...options,
    },
  );
};

export const useUpdateParticipationNotes = (
  id?: string,
  options: UseMutationOptions<Participation, Error, { notes: string }> = {},
) => {
  const updateParticipationCache = useUpdateParticipationCache();

  return useMutation<Participation, Error, { notes: string }>(
    queryUpdate(`/participations/${id}/notes`),
    {
      onSuccess: (p, variable, context) => {
        updateParticipationCache(p);
        if (options.onSuccess) {
          options.onSuccess(p, variable, context);
        }
      },
      ...options,
    },
  );
};

export const useUpdateParticipationTags = (id?: string) => {
  const updateParticipationCache = useUpdateParticipationCache(undefined, true);
  const { data: participation } = useParticipation(id);

  const onMutate = useCallback(
    (params: { tags: string[] }) => {
      if (!participation) {
        return;
      }
      const tags = participation.tags;
      updateParticipationCache({
        ...participation,
        tags: params.tags,
      });
      return tags;
    },
    [updateParticipationCache, participation],
  );
  const onError = useCallback(
    (_e: Error, _prarams: any, context: string[] | undefined) => {
      if (!participation) {
        return;
      }
      updateParticipationCache({
        ...participation,
        tags: context,
      });
    },
    [updateParticipationCache, participation],
  );

  const onSuccess = useCallback(
    (p: Participation) => {
      updateParticipationCache(p);
    },
    [updateParticipationCache],
  );

  return useMutation<
    Participation,
    Error,
    { tags: string[] },
    string[] | undefined
  >(queryUpdate(`/participations/${id}/tags`), {
    onSuccess,
    onError,
    onMutate,
  });
};

export const useContactParticipation = () => {
  return useMutation<
    Participation,
    Error,
    {
      sessionId: string;
      status: ParticipationStatus[];
      message: string;
      subject: string;
    },
    string[] | undefined
  >(queryCreate(`/participations/contact`));
};

export const useContactSmsParticipation = () => {
  return useMutation<
    Participation,
    Error,
    {
      sessionId: string;
      status: ParticipationStatus[];
      message: string;
    },
    string[] | undefined
  >(queryCreate(`/participations/contact-sms`));
};

export const useSetParticipationAsViewed = (id?: string) => {
  const updateParticipationCache = useUpdateParticipationCache();
  const queryClient = useQueryClient();
  return useMutation<Participation, Error, void>(
    queryUpdate(`/participations/${id}/lastSeen`),
    {
      onSuccess: (p) => {
        const participation = queryClient.getQueryData([
          'participations',
          p.id,
        ]);
        if (!participation) {
          return updateParticipationCache(p);
        }
        updateParticipationCache({
          ...participation,
          ...p,
        });
      },
    },
  );
};

export const useParticipantParticipations = (pid?: string) => {
  return useQuery<Participation[]>(
    ['participants', pid, 'participations'],
    queryGet(`/participants/${pid}/participations`),
    {
      enabled: !!pid,
    },
  );
};

export const useMarkAsNotInterested = (pid?: string) => {
  const { data: me } = useMe(true);
  const accountId = me?.id;
  const updateParticipationCache = useUpdateParticipationCache();
  const queryClient = useQueryClient();

  return useMutation<Participation, unknown, void>(
    queryUpdate(`/participations/${pid}/markAsNotInterested`),
    {
      onSuccess: (p) => {
        queryClient.invalidateQueries([
          'accounts',
          accountId,
          'participations',
        ]);
        updateParticipationCache(p, { isUpdateMutation: true });
      },
    },
  );
};

export const useParticipations = () => {
  const { data: me } = useMe(true);
  const accountId = me?.id;

  return useQuery<Participation[]>(
    ['accounts', accountId, 'participations'],
    async () => {
      if (!me) {
        return [];
      }
      const allRes = await Promise.all(
        me.linkedParticipants.map((pid) =>
          queryGet(`/participants/${pid}/participations`)(),
        ),
      );
      return allRes.reduce((list, res) => [...list, ...res], []);
    },
    {
      enabled: !!accountId,
    },
  );
};

export const useCountParticipationByStatus = (sessionId?: string) => {
  return useQuery<Record<ParticipationStatus, number>>(
    ['sessions', sessionId, 'participation-status', 'grouped-count'],
    queryGet(`/sessions/${sessionId}/participation-status/grouped-count`),
    {
      enabled: !!sessionId,
    },
  );
};

export const useSessionParticipationsCount = (
  id?: string,
  status?: ParticipationStatus[] | ParticipationStatus,
  search?: string,
) => {
  const statusList = Array.isArray(status) ? status : [status];

  return useQuery<number>(
    search
      ? ['sessions', id, 'participations', 'count', statusList, search]
      : ['sessions', id, 'participations', 'count', statusList],
    queryGet(`/sessions/${id}/participations/count`, {
      status: statusList,
      search,
    }),
    {
      enabled: !!id,
    },
  );
};

export interface ParticipationStats {
  status: ParticipationStatus;
  source: ParticipationSource;
  platform: string;
  details: string;
  count: number;
}
export const useSessionParticipationsStats = (id?: string) => {
  return useQuery<ParticipationStats[]>(
    ['sessions', id, 'participations', 'stats'],
    queryGet(`/sessions/${id}/participations/stats`),
    {
      enabled: !!id,
    },
  );
};

export const useMoveParticipationToOtherSession = (participationId: string) => {
  const updateParticipationCache = useUpdateParticipationCache();
  const { data: participation } = useParticipation(participationId);
  const queryClient = useQueryClient();
  return useMutation<Participation, any, { sessionId: string }>(
    queryUpdate(`/participations/${participationId}/session`),
    {
      onSuccess: (p) => {
        queryClient.invalidateQueries([
          'sessions',
          participation?.sessionId,
          'participations',
        ]);
        updateParticipationCache(p);
      },
    },
  );
};

export const useViewParticipationPage = (id?: string) => {
  return useQuery<Participation, AxiosError>(
    ['participations', id, 'mark-as-viewed'],
    queryCreate(`/participations/${id}/mark-as-viewed`),
    {
      retry: false,
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
};
